import { Flex } from "@chakra-ui/react";
import { Typography } from "@reconvert/react-ui-component";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import "./App.css";
import Error from "./Error";
import { useAppDispatch, useAppSelector } from "./app/hooks";
import { selectAppState } from "./app/slice/appSlice";
import Loading from "./components/CheckoutPage/components/Loading";
import PageLayout from "./components/PageLayout/PageLayout";
import { ConfirmModalProvider } from "./context/ConfirmModalContext";
import useAppInit from "./hooks/useAppInit";
import "./utils/i18n";
import { APP_PLATFORM, APP_PREFIX } from "./config";
import { useEffect, useMemo, useState } from "react";
import i18n from "./utils/i18n";
import ReviewProviderWrapper from "./ReviewProviderWrapper";
import { createApp } from "@shopify/app-bridge";
import { decodeFbqParam } from "./utils/helpers";
import { useEditorInitialization } from "./hooks/useEditorInitialization";
import { Platform } from "@reconvert/reconvert-utils";

interface ShopifyWindow {
  config: {
    apiKey: string;
    appOrigins: string[];
    debug: Record<string, unknown>;
    disabledFeatures: string[];
    experimentalFeatures: string[];
    locale: string;
    host: string;
    shop: string;
  };
  protocol: Record<string, unknown>;
  origin: string;
  app: Record<string, unknown>;
  environment: {
    mobile: boolean;
    embedded: boolean;
    pos: boolean;
  };
  intents: Record<string, unknown>;
  pos: {
    cart: Record<string, unknown>;
  };
  idToken: () => Promise<string>;
  reviews: {
    request: () => Promise<{ success: boolean, code: string, message: string }>;
  };
  modal: Record<string, unknown>;
  saveBar: Record<string, unknown>;
  scanner: Record<string, unknown>;
  scopes: Record<string, unknown>;
  sidekick: Record<string, unknown>;
  support: Record<string, unknown>;
  toast: Record<string, unknown>;
  webVitals: Record<string, unknown>;
  ready: Record<string, unknown>;
}

declare global {
  interface Window {
    shopify: ShopifyWindow;
  }
}

function App() {
  const app = useMemo(() => window.shopify && createApp({
    apiKey: window.shopify.config.apiKey,
    host: window.shopify.config.host || "",
  }), [window.shopify]);

  const [token] = useState(window.location.search.split("shopifyToken=")[1] || null);
  const [shopifyIdToken] = useState(window.location.search.split("shopifyIdToken=")[1] || null);

  return (
    <BrowserRouter>
      <Routes>
        <Route path={APP_PREFIX} element={<ReviewProviderWrapper app={app} token={token} shopifyToken={shopifyIdToken}><Main /></ReviewProviderWrapper>}></Route>
        <Route path="/Error" element={<Error />}></Route>
      </Routes>
    </BrowserRouter>
  );
}

const Main = () => {
  const { loading, error, activePage } = useAppSelector(selectAppState);
  const dispatch = useAppDispatch();

  useAppInit();

  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const language = queryParams.get("language") || "en";

  // Get fbq parameter to open unified editor from URL params
  const fbqParam = queryParams.get("fbq");
  const decodedFbq = fbqParam || "";

  const params = decodeFbqParam(decodedFbq);
  const { initializeEditor, isInitialized } = useEditorInitialization(params, dispatch);

  // Initialize editor when params are available and not already initialized
  useEffect(() => {
    if (fbqParam && !isInitialized && APP_PLATFORM === Platform.WIX) {
      initializeEditor();
    }
  }, [fbqParam, isInitialized, initializeEditor]);


  if (loading || !activePage) {
    return (
      <Flex height="100vh" justifyContent="center" alignItems="center">
        <Loading />
      </Flex>
    );
  }

  if (error) {
    return (
      <Typography textAlign="center" variant="h2">
        Oops!! Access denied.
      </Typography>
    );
  }

  return (
    <ConfirmModalProvider>
      <PageLayout />
    </ConfirmModalProvider>
  );
};

export default App;
